'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { Filter } from '@/models/common';
import services from '@/services';
import { useMainStore } from '@/store';
import { DEFAULT_FILTERS } from '../constants';
const CLIENT_ID_FILTER_FIELD = 'creator.site.client_id';
const CLOSED_STATUS_FILTER_FIELD = 'status.label';
const CLOSED_STATUS_VALUE = 'closed';
const SURVEY_COUNT_STALE_TIME_MS = 30 * 1000;
function hasClosedStatusFilter(filters: Filter[]): boolean {
  return filters.some(filter => filter.field === CLOSED_STATUS_FILTER_FIELD && filter.operator === 'in' && Array.isArray(filter.value) && filter.value.length === 1 && (filter.value as string[])[0] === CLOSED_STATUS_VALUE);
}
function buildSurveyFiltersWithClientId(baseFilters: Filter[], clientId: string): Filter[] {
  return [...baseFilters, {
    field: CLIENT_ID_FILTER_FIELD,
    operator: '=',
    value: clientId
  }];
}
function computeActiveFilters(currentFilters: Filter[]): Filter[] {
  return hasClosedStatusFilter(currentFilters) ? [DEFAULT_FILTERS.EXCLUDE_CLOSED] : currentFilters;
}
async function fetchSurveyCount(filters: Filter[], clientId: string, userId: string, search: string): Promise<number> {
  const filtersWithClientId = buildSurveyFiltersWithClientId(filters, clientId);
  const result = await services.survey.getSurveys({
    page: 1,
    filters: filtersWithClientId,
    value: search,
    userId,
    optimizedForCount: true
  });
  return result.total ?? result.meta?.total ?? 0;
}
const CLOSED_SURVEY_FILTERS = [DEFAULT_FILTERS.INCLUDE_CLOSED];
export const useSurveyCounts = (t0, t1) => {
  const $ = _c(28);
  let t2;
  if ($[0] !== t0) {
    t2 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const currentFilters = t2;
  const search = t1 === undefined ? "" : t1;
  const {
    currentUser
  } = useMainStore();
  const clientId = currentUser?.site?.client_id;
  const userId = currentUser?.id;
  let t3;
  let t4;
  if ($[2] !== currentFilters) {
    t4 = computeActiveFilters(currentFilters);
    $[2] = currentFilters;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  t3 = t4;
  const activeFilters = t3;
  let t5;
  let t6;
  if ($[4] !== activeFilters || $[5] !== clientId || $[6] !== search || $[7] !== userId) {
    t5 = ["active-count", clientId, userId, activeFilters, search];
    t6 = () => fetchSurveyCount(activeFilters, clientId, userId ?? "", search);
    $[4] = activeFilters;
    $[5] = clientId;
    $[6] = search;
    $[7] = userId;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  const t7 = !!clientId && !!userId;
  let t8;
  if ($[10] !== t5 || $[11] !== t6 || $[12] !== t7) {
    t8 = {
      queryKey: t5,
      queryFn: t6,
      enabled: t7,
      staleTime: SURVEY_COUNT_STALE_TIME_MS,
      placeholderData: _temp
    };
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const {
    data: activeSurveysCount,
    isLoading: isLoadingActive
  } = useQuery(t8);
  let t10;
  let t9;
  if ($[14] !== clientId || $[15] !== search || $[16] !== userId) {
    t9 = ["closed-count", clientId, userId, CLOSED_SURVEY_FILTERS, search];
    t10 = () => fetchSurveyCount(CLOSED_SURVEY_FILTERS, clientId, userId ?? "", search);
    $[14] = clientId;
    $[15] = search;
    $[16] = userId;
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  const t11 = !!clientId && !!userId;
  let t12;
  if ($[19] !== t10 || $[20] !== t11 || $[21] !== t9) {
    t12 = {
      queryKey: t9,
      queryFn: t10,
      enabled: t11,
      staleTime: SURVEY_COUNT_STALE_TIME_MS,
      placeholderData: _temp2
    };
    $[19] = t10;
    $[20] = t11;
    $[21] = t9;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const {
    data: closedSurveysCount,
    isLoading: isLoadingClosed
  } = useQuery(t12);
  const t13 = activeSurveysCount ?? 0;
  const t14 = closedSurveysCount ?? 0;
  let t15;
  if ($[23] !== isLoadingActive || $[24] !== isLoadingClosed || $[25] !== t13 || $[26] !== t14) {
    t15 = {
      activeSurveysCount: t13,
      closedSurveysCount: t14,
      isLoadingActive,
      isLoadingClosed
    };
    $[23] = isLoadingActive;
    $[24] = isLoadingClosed;
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  return t15;
};
function _temp(previousData) {
  return previousData;
}
function _temp2(previousData_0) {
  return previousData_0;
}